import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Skeleton,
  Stack,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import { networkID, sumWeiToEther, compareWei } from "../../../../static/utils";
import { getNetworkCurrency, DEFAULT_CURRENCY, lotteryTypeMapper, LOTTERY_TYPE_IDS } from "../../../../static/Constants";

// Theme & Common UI
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";

// Subcomponents
import ResultsHeroBanner from "./components/ResultsHeroBanner";
import ResultsFilters from "./components/ResultsFilters";
import ResultsCard from "./components/ResultsCard";
import ResultsEmptyState from "./components/ResultsEmptyState";
import ResultsTransparencyInfo from "./components/ResultsTransparencyInfo";

import "./ResultsStyle.css";

const NoWinnerAddress = "0x0000000000000000000000000000000000000000";

const withTimeout = (promise, ms = 4500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const Results = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lotteryList, setLotteryList] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});

  // Filters & Sorting
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId]
  );

  /**
   * Fetch All Finalized On-Chain Draw Results (status === "2")
   */
  const fetchLotteryResults = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7500);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3 && web3.eth && web3.eth.net && web3.eth.net.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Get total lottery count
        const totalIdsRaw = await withTimeout(
          cinstance.methods.lotteryId().call(),
          4000
        ).catch((err) => {
          console.warn("[Lottery Results] lotteryId() call failed:", err);
          return 0;
        });

        const totalIds = Number(totalIdsRaw || 0);
        const lotteryCalls = [];

        for (let i = 1; i <= totalIds; i++) {
          lotteryCalls.push(
            withTimeout(cinstance.methods.lottery(i).call(), 4000).catch((err) => {
              console.log("lottery call error", i, err);
              return null;
            })
          );
        }

        const rawResults = await Promise.all(lotteryCalls);

        // Filter strictly for Result Done (status === "2")
        const completedLotteries = rawResults
          .filter(
            (response) =>
              response &&
              String(response.status) === "2" &&
              Boolean(response.lotteryId)
          )
          .sort((a, b) => Number(b.lotteryId) - Number(a.lotteryId));

        // 2. Fetch lotteryDates concurrently for all completed lotteries
        const datesCalls = completedLotteries.map((item) =>
          withTimeout(cinstance.methods.lotteryDates(item.lotteryId).call(), 3500)
            .then((dateRes) => ({ lotteryId: item.lotteryId, dates: dateRes }))
            .catch(() => ({ lotteryId: item.lotteryId, dates: null }))
        );

        const datesResults = await Promise.all(datesCalls);
        const datesMap = {};
        datesResults.forEach((d) => {
          if (d && d.dates) {
            datesMap[d.lotteryId] = d.dates;
          }
        });

        // Merge dates into completed lotteries
        const mergedList = completedLotteries.map((item) => {
          const dateInfo = datesMap[item.lotteryId] || {};
          return {
            ...item,
            ...dateInfo,
            drawTime: dateInfo.drawTime || item.drawTime || item.deployedOn,
          };
        });

        setLotteryList(mergedList);

        // 3. Fetch Partner profiles concurrently for unique creator addresses
        const uniquePartners = [
          ...new Set(
            mergedList
              .map((item) => item.partnerAddress || item.partner)
              .filter(
                (addr) =>
                  addr &&
                  addr !== NoWinnerAddress &&
                  addr !== "0x0"
              )
          ),
        ];

        if (uniquePartners.length > 0) {
          const partnerPromises = uniquePartners.map((addr) =>
            withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3500)
              .then((res) => ({ [addr]: res }))
              .catch((err) => {
                console.log("partnerbyaddr error", err);
                return { [addr]: null };
              })
          );

          const partnerEntries = await Promise.all(partnerPromises);
          const mergedPartnerMap = partnerEntries.reduce(
            (acc, curr) => Object.assign(acc, curr),
            {}
          );
          setPartnerMap(mergedPartnerMap);
        } else {
          setPartnerMap({});
        }

        if (isRefresh) {
          toast.success("Lottery draw results refreshed!");
        }
      } catch (err) {
        console.error("[Lottery Results] Error loading results:", err);
        toast.error("Failed to load on-chain lottery results.");
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLotteryResults();
  }, [fetchLotteryResults]);

  /**
   * Computed Summary Stats & Game Type Counts
   */
  const { totalCompleted, totalPrizeDistributed, uniqueWinnersCount, countsByType } =
    useMemo(() => {
      const winnersSet = new Set();
      const counts = {
        all: lotteryList.length,
        [LOTTERY_TYPE_IDS.SPINBET]: 0,
        [LOTTERY_TYPE_IDS.MINEBET]: 0,
        [LOTTERY_TYPE_IDS.MEGABET]: 0,
        [LOTTERY_TYPE_IDS.ROCKETBET]: 0,
      };
      const prizeList = [];

      lotteryList.forEach((item) => {
        const typeId = Number(item.lotteryType || 0);
        if (counts[typeId] !== undefined) {
          counts[typeId] += 1;
        }

        if (item.totalPrize) {
          prizeList.push(item.totalPrize);
        }

        const winner = item.lotteryWinner;
        if (
          winner &&
          winner !== NoWinnerAddress &&
          winner !== "" &&
          winner !== "0x0"
        ) {
          winnersSet.add(winner.toLowerCase());
        }
      });
      console.log({prizeList, winnersSet})

      return {
        totalCompleted: lotteryList.length,
        totalPrizeDistributed: sumWeiToEther(prizeList, 5),
        uniqueWinnersCount: winnersSet.size,
        countsByType: counts,
      };
    }, [lotteryList]);

  /**
   * Filtered & Sorted Draw Results
   */
  const filteredLotteries = useMemo(() => {
    let list = [...lotteryList];

    // 1. Category Tab Filter
    if (activeTab !== "all") {
      list = list.filter((item) => String(item.lotteryType || "0") === activeTab);
    }

    // 2. Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const idMatch = `${item.lotteryId}`.includes(q);
        const winnerMatch = (item.lotteryWinner || "").toLowerCase().includes(q);
        const partnerAddr = (item.partnerAddress || item.partner || "").toLowerCase();
        const partnerInfo = partnerMap[item.partnerAddress || item.partner];
        const partnerNameMatch =
          partnerInfo &&
          ((partnerInfo.name && partnerInfo.name.toLowerCase().includes(q)) ||
            (partnerInfo.companyName &&
              partnerInfo.companyName.toLowerCase().includes(q)));
        const typeNameMatch = (lotteryTypeMapper[item.lotteryType] || "")
          .toLowerCase()
          .includes(q);

        return (
          idMatch ||
          winnerMatch ||
          partnerAddr.includes(q) ||
          partnerNameMatch ||
          typeNameMatch
        );
      });
    }

    // 3. Sorting
    list.sort((a, b) => {
      const idA = Number(a.lotteryId || 0);
      const idB = Number(b.lotteryId || 0);
      const drawA = Number(a.drawTime || a.deployedOn || 0);
      const drawB = Number(b.drawTime || b.deployedOn || 0);
      const prizeComp = compareWei(a.totalPrize, b.totalPrize);

      switch (sortBy) {
        case "oldest":
          return drawA - drawB || idA - idB;
        case "highestPrize":
          return -prizeComp || idB - idA;
        case "lowestPrize":
          return prizeComp || idA - idB;
        case "newest":
        default:
          return drawB - drawA || idB - idA;
      }
    });

    return list;
  }, [lotteryList, activeTab, searchQuery, sortBy, partnerMap]);

  const handleResetFilters = () => {
    setActiveTab("all");
    setSearchQuery("");
    setSortBy("newest");
  };

  const hasActiveFilters =
    activeTab !== "all" || searchQuery.trim() !== "" || sortBy !== "newest";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container
        maxWidth="xl"
        className="results-page-container"
        sx={{ pt: { xs: "90px", md: "100px" }, pb: 8, flexGrow: 1 }}
      >
        {/* 1. Hero Header & Live Stats Banner */}
        <ResultsHeroBanner
          totalCompleted={totalCompleted}
          totalPrizeDistributed={totalPrizeDistributed}
          uniqueWinnersCount={uniqueWinnersCount}
          currency={currency}
        />

        {/* 2. Game Tabs, Search & Sorting Controls */}
        <ResultsFilters
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          countsByType={countsByType}
          onRefresh={() => fetchLotteryResults(true)}
          loading={loading || refreshing}
        />

        {/* 3. Loading Skeletons */}
        {loading && (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((idx) => (
              <Grid item xs={12} sm={6} lg={6} key={`results-skel-${idx}`}>
                <Box
                  sx={{
                    bgcolor: colors.dashboard.cardBg,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    p: 3,
                    height: 380,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: borderRadiusPx.md }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="50%" height={24} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </Box>
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    height={72}
                    sx={{ borderRadius: borderRadiusPx.lg }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={64}
                    sx={{ borderRadius: borderRadiusPx.lg }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* 4. Empty State */}
        {!loading && filteredLotteries.length === 0 && (
          <ResultsEmptyState
            onReset={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        )}

        {/* 5. Results Cards Grid */}
        {!loading && filteredLotteries.length > 0 && (
          <Grid container spacing={3}>
            {filteredLotteries.map((item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={6}
                  key={`result-lottery-${item.lotteryId}`}
                >
                  <ResultsCard
                    lottery={item}
                  />
                </Grid>
            ))}
          </Grid>
        )}

        {/* 6. On-Chain Transparency & FAQs */}
        {/* <ResultsTransparencyInfo /> */}
      </Container>
    </Box>
  );
};

export default Results;
